import React from "react";
import { Link } from "react-router-dom";
import { ASSETS } from "@/lib/assets";
import { Skull, ChevronRight } from "lucide-react";
import LeaderboardPanel from "@/components/LeaderboardPanel";

export default function Landing() {
  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* Hero background */}
      <div className="absolute inset-0 z-0">
        <img src={ASSETS.hero} alt="" className="w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0"
             style={{ background: "linear-gradient(180deg, rgba(3,3,3,0.55) 0%, rgba(3,3,3,0.85) 60%, rgba(3,3,3,1) 100%)" }} />
      </div>

      {/* Top nav */}
      <nav className="relative z-10 px-6 sm:px-12 py-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Skull className="w-5 h-5 text-blood" />
          <span className="tac-label" data-testid="brand-mark">ZOMBIE USURPER // 0.1</span>
        </div>
        <Link to="/auth" className="btn-ghost" data-testid="nav-enter-link">ENTER ↗</Link>
      </nav>

      {/* Hero */}
      <main className="relative z-10 px-6 sm:px-12 lg:px-20 pt-12 sm:pt-20 lg:pt-32 pb-24 max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-8 fade-up">
            <p className="tac-label mb-8" data-testid="hero-subtitle">
              FROM GRUNT // TO KING // TO EMPEROR
            </p>
            <h1 className="font-serif-h text-5xl sm:text-6xl lg:text-8xl font-bold leading-[0.95] tracking-tight text-bone uppercase">
              You died.<br />
              <span className="text-blood">You woke up.</span><br />
              The world is meat.
            </h1>
            <p className="mt-10 max-w-xl text-base sm:text-lg leading-relaxed text-[var(--text-main)] fade-up delay-1"
               data-testid="hero-description">
              You start as a grunt — a hungry corpse following orders. You have no weapons. The humans do.
              Every zombie you lose is gone forever. Every human you tear apart rises beside you.
              <br /><br />
              Stay a grunt. Or climb. Become Squad Leader. Lieutenant. Warlord. Crown Prince.
              Then choose: kneel before the King, or rip his throne from the bone.
            </p>

            <div className="mt-14 flex flex-wrap gap-4 fade-up delay-2">
              <Link to="/auth" className="btn-blood" data-testid="cta-rise-button">
                RISE FROM THE DIRT <ChevronRight className="w-4 h-4" />
              </Link>
              <Link to="/auth" state={{ goTo: "/new-game" }} className="btn-bone" data-testid="cta-new-game-button">
                NEW GAME
              </Link>
              <a href="#mechanics" className="btn-ghost" data-testid="cta-mechanics-link">
                THE LAWS OF THE DEAD
              </a>
            </div>
          </div>

          {/* Side stats */}
          <div className="lg:col-span-4 fade-up delay-3 flex flex-col gap-3">
            <SideStat label="DEATH" value="YOUR CHOICE" />
            <SideStat label="WORLD" value="15–31 REGIONS" />
            <SideStat label="NARRATIVE" value="LIVE-WRITTEN BY THE COMMANDER" />
            <SideStat label="CREATED BY" value="SAMUEL STEWART" />
          </div>
        </div>

        {/* Mechanics block */}
        <section id="mechanics" className="mt-32 lg:mt-44 grid grid-cols-1 md:grid-cols-3 gap-8">
          <Mechanic
            number="I."
            title="Permadeath Horde"
            body="Zombies don't respawn. Each one lost is a tally on a wall you'll never clean. Send too many, and your column collapses — and the humans will wait for you in the next valley with stronger guns." />
          <Mechanic
            number="II."
            title="Risk vs Reward"
            body="Stay back: low casualties, low growth, low morale. Lead the charge: bigger horde, more dead under you. Engagement and commitment are dials you set every battle. The dead don't forgive overreach." />
          <Mechanic
            number="III."
            title="The Suggestion"
            body="Until Crown Prince, the world map is yours to LOOK at, not command. You suggest a target to your superior. They listen — sometimes. Climb the ladder. Earn the throne. Or break it." />
        </section>

        {/* Public weekly leaderboard */}
        <section className="mt-24 lg:mt-32 max-w-3xl mx-auto fade-up" data-testid="landing-leaderboard">
          <h2 className="font-serif-h text-3xl sm:text-4xl text-bone uppercase mb-6 text-center">
            THE WEEK'S LARGEST <span className="text-blood">HORDES</span>
          </h2>
          <LeaderboardPanel readOnly={true} />
        </section>

        <div className="mt-32 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 fade-up">
          <Link to="/hall" className="font-mono text-[0.7rem] tracking-[0.22em] text-muted-h hover:text-blood transition-colors"
                data-testid="footer-hall-link">
            HALL OF HORDES
          </Link>
          <span className="text-muted-h/40">·</span>
          <Link to="/contact" className="font-mono text-[0.7rem] tracking-[0.22em] text-muted-h hover:text-blood transition-colors"
                data-testid="footer-contact-link">
            CONTACT THE BUILDER
          </Link>
          <span className="text-muted-h/40">·</span>
          <span className="font-mono text-[0.7rem] tracking-[0.22em] text-muted-h">
            SAMUEL STEWART · 2026
          </span>
        </div>
        <p className="mt-6 tac-label text-center text-muted-h" data-testid="footer-tag">
          NO RESPAWNS // NO MERCY // NO DAYLIGHT
        </p>
      </main>
    </div>
  );
}

const SideStat = ({ label, value }) => (
  <div className="tactical-card flex items-baseline justify-between">
    <span className="tac-label">{label}</span>
    <span className="font-mono text-xs text-bone">{value}</span>
  </div>
);

const Mechanic = ({ number, title, body }) => (
  <div className="border-l-2 border-[var(--border-blood)] pl-6">
    <div className="font-serif-h text-blood text-3xl mb-3">{number}</div>
    <h3 className="font-serif-h text-2xl text-bone mb-3 uppercase">{title}</h3>
    <p className="text-sm leading-relaxed text-[var(--text-main)]">{body}</p>
  </div>
);
